import React, { useState, useEffect, useRef } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { TextField, Button } from "@mui/material";
import { CameraIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";

/**
 * QRCodeScanner - A reusable QR code scanner component
 * 
 * @param {boolean} open - Whether the scanner is open/visible
 * @param {Function} onScan - Callback when a QR code is scanned (receives the extracted code/product ID)
 * @param {Function} onClose - Callback when scanner is closed
 * @param {string} placeholder - Placeholder text for manual input
 * @param {boolean} showBackButton - Whether to show a back button
 * @param {Function} onBack - Callback when back button is clicked
 */
const QRCodeScanner = ({
  open = false,
  onScan,
  onClose,
  placeholder = "Skriv produktnummer eller skann QR-kodewdwd",
  showBackButton = true,
  onBack,
  isSearching = false,
}) => {
  const [cameraMode, setCameraMode] = useState(false);
  const [manualInput, setManualInput] = useState("");
  const [isScanning, setIsScanning] = useState(false);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const isStartingCameraRef = useRef(false);
  const autoSearchTimeoutRef = useRef(null);

  /**
   * Extract product ID from URL or return the code as-is
   * Supports formats like:
   * - http://localhost:3000/dashboard/products/1
   * - https://promang.no/dashboard/products/1
   * - /dashboard/products/1
   * - Just a number or code
   */
  const extractProductId = (scannedCode) => {
    if (!scannedCode || scannedCode.trim() === "") {
      return null;
    }

    const trimmedCode = scannedCode.trim();

    // Try to match URL pattern: .../dashboard/products/{id}
    const urlPattern = /\/dashboard\/products\/(\d+)/;
    const match = trimmedCode.match(urlPattern);

    if (match && match[1]) {
      // Found product ID in URL
      return match[1];
    }

    // If it's just a number, return it
    if (/^\d+$/.test(trimmedCode)) {
      return trimmedCode;
    }

    // Otherwise, return the code as-is (could be item_number or other identifier)
    return trimmedCode;
  };

  /**
   * Handle scanned QR code
   */
  const handleScannedCode = (scannedCode) => {
    const extractedCode = extractProductId(scannedCode);
    
    if (extractedCode) {
      // Stop scanning if camera is active
      if (cameraMode && html5QrCodeRef.current) {
        stopCamera();
      }
      
      // Call the onScan callback with the extracted code
      if (onScan) {
        onScan(extractedCode);
      }
      
      // Reset state
      setManualInput("");
      setCameraMode(false);
    } else {
      toast.error("Ugyldig QR-kode");
    }
  };

  /**
   * Start camera scanning
   */
  const startCamera = async () => {
    if (isScanning || isStartingCameraRef.current) return;
    
    isStartingCameraRef.current = true;

    // Wait a bit for the DOM element to be ready
    await new Promise(resolve => setTimeout(resolve, 300));

    const element = document.getElementById("qr-reader");
    if (!element) {
      console.error("QR reader element not found");
      toast.error("Kunne ikke finne scanner-element");
      setIsScanning(false);
      setCameraMode(false);
      isStartingCameraRef.current = false;
      return;
    }

    try {
      setIsScanning(true);
      
      // Clear any existing instance
      if (html5QrCodeRef.current) {
        try {
          await html5QrCodeRef.current.stop();
          await html5QrCodeRef.current.clear();
        } catch (e) {
          // Ignore cleanup errors
        }
      }

      const html5QrCode = new Html5Qrcode("qr-reader");
      html5QrCodeRef.current = html5QrCode;

      // Calculate qrbox size based on viewport (use 80% of smaller dimension)
      const viewportWidth = element.clientWidth || 300;
      const viewportHeight = element.clientHeight || 256;
      const qrboxSize = Math.min(viewportWidth, viewportHeight) * 0.8;

      // Try different camera configurations
      const cameraConfigs = [
        { facingMode: "environment" }, // Back camera (preferred)
        { facingMode: "user" }, // Front camera (fallback)
      ];

      let cameraStarted = false;
      let lastError = null;

      for (const config of cameraConfigs) {
        try {
          await html5QrCode.start(
            config,
            {
              fps: 10,
              qrbox: { width: Math.floor(qrboxSize), height: Math.floor(qrboxSize) },
              aspectRatio: 1.0,
              disableFlip: false,
              videoConstraints: {
                facingMode: config.facingMode,
                width: { ideal: 1280 },
                height: { ideal: 720 },
              },
            },
            (decodedText, decodedResult) => {
              // Success callback
              console.log("QR Code scanned:", decodedText);
              handleScannedCode(decodedText);
            },
            (errorMessage) => {
              // Error callback - log for debugging but don't show to user
              // This is called frequently during scanning attempts
              if (errorMessage && !errorMessage.includes("NotFoundException")) {
                // Only log non-trivial errors
                console.debug("QR scan attempt:", errorMessage);
              }
            }
          );
          cameraStarted = true;
          console.log("Camera started successfully with config:", config);
          break;
        } catch (error) {
          console.warn(`Failed to start camera with ${config.facingMode}:`, error);
          lastError = error;
          // Try next configuration
          try {
            await html5QrCode.stop();
            await html5QrCode.clear();
          } catch (e) {
            // Ignore cleanup errors
          }
        }
      }

      if (!cameraStarted) {
        throw lastError || new Error("Kunne ikke starte kamera med noen konfigurasjon");
      }

      isStartingCameraRef.current = false;
    } catch (error) {
      console.error("Error starting camera:", error);
      
      let errorMessage = "Kunne ikke åpne kamera. ";
      if (error.name === "NotAllowedError" || error.message?.includes("permission")) {
        errorMessage += "Sjekk at du har gitt kamera-tillatelse.";
      } else if (error.name === "NotFoundError" || error.message?.includes("camera")) {
        errorMessage += "Ingen kamera funnet.";
      } else if (error.message) {
        errorMessage += error.message;
      } else {
        errorMessage += "Sjekk at du har gitt tillatelse.";
      }
      
      toast.error(errorMessage);
      setIsScanning(false);
      setCameraMode(false);
      isStartingCameraRef.current = false;
    }
  };

  /**
   * Stop camera scanning
   */
  const stopCamera = async () => {
    if (html5QrCodeRef.current) {
      try {
        const scanner = html5QrCodeRef.current;
        await scanner.stop();
        await scanner.clear();
      } catch (error) {
        // Ignore errors when stopping (camera might already be stopped)
        console.debug("Error stopping camera (can be ignored):", error);
      }
      html5QrCodeRef.current = null;
    }
    setIsScanning(false);
    setCameraMode(false);
    isStartingCameraRef.current = false;
  };

  /**
   * Handle manual input submission
   */
  const handleManualSubmit = () => {
    if (manualInput.trim()) {
      handleScannedCode(manualInput);
    } else {
      toast.error("Skriv inn eller skann en QR-kode");
    }
  };

  /**
   * Auto-start camera when camera mode is enabled
   */
  useEffect(() => {
    if (cameraMode && !isScanning && !isStartingCameraRef.current && open) {
      startCamera();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraMode, open]);

  /**
   * Auto-search when user types product number (debounced)
   */
  useEffect(() => {
    // Clear previous timeout
    if (autoSearchTimeoutRef.current) {
      clearTimeout(autoSearchTimeoutRef.current);
    }

    // Don't auto-search if input is empty or camera is active
    if (!manualInput || cameraMode || !open) {
      return;
    }

    const trimmedInput = manualInput.trim();
    
    // Only auto-search if:
    // - Manual input has at least 2 characters
    // - It looks like a product number (alphanumeric, no spaces, no slashes)
    // - Not a URL (doesn't contain http://, https://, or /dashboard/)
    const isUrl = trimmedInput.includes("http://") || 
                  trimmedInput.includes("https://") || 
                  trimmedInput.includes("/dashboard/");
    
    if (trimmedInput.length >= 2 && 
        /^[a-zA-Z0-9]+$/.test(trimmedInput) &&
        !isUrl) {
      autoSearchTimeoutRef.current = setTimeout(() => {
        // Only search if input hasn't changed (user stopped typing)
        if (manualInput.trim() === trimmedInput) {
          handleScannedCode(trimmedInput);
        }
      }, 500); // Wait 500ms after user stops typing
    }

    // Cleanup timeout on unmount or when input changes
    return () => {
      if (autoSearchTimeoutRef.current) {
        clearTimeout(autoSearchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualInput, cameraMode, open]);

  /**
   * Cleanup camera when component unmounts or closes
   */
  useEffect(() => {
    if (!open) {
      stopCamera();
      setManualInput("");
      setCameraMode(false);
    }

    return () => {
      stopCamera();
    };
  }, [open]);

  if (!open) {
    return null;
  }

  return (
    <div className="space-y-4">
      {showBackButton && (
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={() => {
              stopCamera();
              if (onBack) {
                onBack();
              } else if (onClose) {
                onClose();
              }
            }}
            className="text-gray-600 hover:text-gray-800"
          >
            ← Tilbake
          </button>
        </div>
      )}

      {/* Camera Mode */}
      {cameraMode && (
        <div className="space-y-4">
          <div className="relative w-full h-64 bg-black rounded-lg overflow-hidden">
            <div id="qr-reader" className="w-full h-full"></div>
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="border-2 border-white rounded-lg" style={{ width: "80%", height: "80%", maxWidth: "300px", maxHeight: "300px" }}></div>
            </div>
            {isScanning && (
              <div className="absolute top-2 left-2 bg-green-500 text-white px-2 py-1 rounded text-xs">
                Skanner...
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={stopCamera}
            className="w-full px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-gray-700 font-medium transition-colors"
          >
            Stopp kamera
          </button>
        </div>
      )}

      {/* Manual Input Mode */}
      {!cameraMode && (
        <div className="space-y-3">
          <div className="relative">
            <TextField
              fullWidth
              variant="outlined"
              placeholder={placeholder}
              value={manualInput}
              onChange={(e) => setManualInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter" && !isSearching) {
                  handleManualSubmit();
                }
              }}
              disabled={isSearching}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "0.5rem",
                  backgroundColor: "#f9fafb",
                  paddingRight: "3rem",
                  "&:hover": {
                    backgroundColor: "#f3f4f6",
                  },
                  "&.Mui-focused": {
                    backgroundColor: "#ffffff",
                  },
                },
              }}
              autoFocus
            />
            {!isSearching && (
              <button
                type="button"
                onClick={() => {
                  setCameraMode(true);
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="Åpne kamera for QR-skanning"
              >
                <CameraIcon className="h-5 w-5" />
              </button>
            )}
            {isSearching && (
              <div className="absolute right-2 top-1/2 -translate-y-1/2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
              </div>
            )}
          </div>
          {manualInput && !isSearching && (
            <button
              type="button"
              onClick={handleManualSubmit}
              className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              Søk
            </button>
          )}
          {isSearching && (
            <div className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg font-medium text-center">
              <div className="flex items-center justify-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Søker...</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QRCodeScanner;

